import React, { useEffect, useState } from 'react';

import { Checkbox, Container } from '@mui/material';

import { YesNoBtnRow, LbFormControlLabel } from '@infoods/lifebase-ui';
import classes from './NgzSelectSubscription.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { BlackBgRow } from '../../../shared/components/ui-components';
import { PageTitle } from './PageTitle/PageTitle';
import { SelectSubscriptionBlock } from '../../containers/SelectSubscriptionBlock/SelectSubscriptionBlock';
import { pathQueryParams } from '../../constants/pathQueryParams';
import { getRouteParam, goTo } from '../../../shared/utils/angularUtils';
import { getAccessParamsFromUrl } from '../../../shared/utils/apiUtils';
import { NgzPageNotFound } from '../../../shared/containers/NgzPageNotFound/NgzPageNotFound';
import {
  GetCustomerCoachDataResponse,
  signUpSubscriptionsService,
} from '../../services/signUpSubscriptionsService';
import { checkoutService } from '../../services/checkoutService';
import { getPriceView } from '../../../shared/utils/stringUtils';
import { SIGN_UP_PAYMENT_RESULT_TYPE } from '../../constants/signUpPaymentResultType';
import { CoachCard } from '../../../customer-management';
import { useSelectSubscription } from '../../hooks/useSelectSubscription';
import { getFlagsObject } from '../../../shared/utils/sortingAndGroupingUtil';
import { BILLING_TYPE } from '../../../shared/constants/user-info/billingType';

// almost the same as SelectSubscriptionStep
const NgzSelectSubscription = () => {
  const [coachInfo, setCoachInfo] = useState<GetCustomerCoachDataResponse | null>(null);
  const [separateBillingAcknowledged, setSeparateBillingAcknowledged] = useState(false);

  const onApplyPromoCode = async (promoCode: string) => {
    if (!coachInfo) {
      throw new Error('Not all required data is set');
    }

    return signUpSubscriptionsService.applyPromoCode(
      customerEmail,
      coachInfo.entityId,
      promoCode,
    );
  };

  const {
    subscriptions,
    discountPercent,
    selectedSubscription,
    selectedSubscriptionTypeFlags,
    isNonPaidSubscription,
    applyPromoCode,
    setSubscriptions,
    setSelectedSubscriptionId,
  } = useSelectSubscription({ onApplyPromoCode });

  const { accessParams, allParamsExist } = getAccessParamsFromUrl();
  const customerEmail = getRouteParam('email');

  const getPageData = async () => {
    if (!allParamsExist) {
      throw new Error('Not all required data is set');
    }

    const [coachData, subscriptionsInfo] = await Promise.all([
      signUpSubscriptionsService.getCustomerCoachData(accessParams),
      signUpSubscriptionsService.getSubscriptionsForReactivation(accessParams),
    ]);

    setCoachInfo(coachData);
    setSubscriptions(subscriptionsInfo);
  };

  useEffect(() => {
    getPageData();
  }, []);

  if (!allParamsExist) {
    return <NgzPageNotFound />;
  }

  const billingTypeFlags = getFlagsObject(BILLING_TYPE, [coachInfo?.billingType]);

  const nonPaidYesButtonText = 'Submit Form';

  const yesButtonText = `Submit Form & Pay ${getPriceView(
    selectedSubscription?.monthlyRenewalPriceFinal,
  )}`;

  const submitButtonText =
    isNonPaidSubscription || billingTypeFlags.SELF_BILLING
      ? nonPaidYesButtonText
      : yesButtonText;

  const isSubmitButtonDisabled =
    !selectedSubscription ||
    (selectedSubscriptionTypeFlags.SELF_BILLING && !separateBillingAcknowledged);

  const handleNext = async () => {
    if (!selectedSubscription || !coachInfo) {
      throw new Error('Not all required data is set');
    }

    const {
      redirectStatus,
      subscriptionType,
      paymentTimeEpoch,
      senderName,
      entityId,
      email,
      paymentResultType,
    } = pathQueryParams;

    if (isNonPaidSubscription) {
      const result = await checkoutService.createNonPaidSubscription(
        coachInfo.entityId,
        customerEmail,
        selectedSubscription.id,
      );

      goTo('/payment-result', {
        params: {
          [redirectStatus]: 'succeeded',
          [subscriptionType]: selectedSubscription.subscriptionType,
          [paymentTimeEpoch]: result.paymentTimeEpoch,
          [senderName]: result.senderName,
          [paymentResultType]: SIGN_UP_PAYMENT_RESULT_TYPE.REACTIVATION,
        },
      });
    } else {
      goTo('/checkout', {
        params: {
          [entityId]: coachInfo.entityId,
          [email]: customerEmail,
          [pathQueryParams.selectedSubscriptionId]: selectedSubscription.id,
          [pathQueryParams.subscriptionType]: selectedSubscription.subscriptionType,
          [paymentResultType]: SIGN_UP_PAYMENT_RESULT_TYPE.REACTIVATION,
        },
      });
    }
  };

  return (
    <Container maxWidth="sm" className={classes.container}>
      <BlackBgRow bgRowHeight="404px">
        {coachInfo && (
          <>
            <PageTitle billingType={coachInfo.billingType} />
            <CoachCard coachInfo={coachInfo} consultationType={null} />
          </>
        )}
        <SelectSubscriptionBlock
          subscriptions={subscriptions}
          discountPercent={discountPercent}
          onApplyPromoCode={applyPromoCode}
          selectedSubscriptionId={selectedSubscription?.id || null}
          setSelectedSubscriptionId={setSelectedSubscriptionId}
          showPromoCodeCard={!billingTypeFlags.SELF_BILLING}
        />
        {selectedSubscriptionTypeFlags?.SELF_BILLING && (
          <LbFormControlLabel
            label="I understand that I will be billed separately by my coach"
            control={
              <Checkbox
                checked={separateBillingAcknowledged}
                onChange={(_, checked) => setSeparateBillingAcknowledged(checked)}
                color="primary"
              />
            }
          />
        )}
        <YesNoBtnRow
          yesText={submitButtonText}
          yesHandler={handleNext}
          yesDisabled={isSubmitButtonDisabled}
          className={classes.buttonRow}
        />
      </BlackBgRow>
    </Container>
  );
};

angularizeWithProviders(NgzSelectSubscription, 'ngzSelectSubscription', {});

export { NgzSelectSubscription };
